import * as React from 'react';
import { Box, Typography, Popper, Fade, useTheme } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const mod = (n, m) => ((n % m) + m) % m;

// Normalización defensiva para comparar strings
const norm = (s) => (s ?? '').toString().trim().toLowerCase();

function useWheelThrottled(handler, delay = 160) {
  const lastRef = React.useRef(0);
  return React.useCallback((e) => {
    const now = Date.now();
    if (now - lastRef.current < delay) return;
    lastRef.current = now;
    handler(e);
  }, [handler, delay]);
}

/**
 * Props:
 * - value: string
 * - items: string[]
 * - onChange(nextValue: string): void
 * - onOpen({ currentTarget: HTMLElement }): void
 * - isMenuOpen: boolean
 * - textSx
 * - prefix
 * - peekDurationMs?: number (default 1800)
 */
export const WheelPeekTrigger = ({
  value,
  items,
  onChange,
  onChangeIndex,
  valueIndex,   
  onOpen,
  isMenuOpen,
  textSx,
  prefix = '',
  peekDurationMs = 1800,
}) => {
  const theme = useTheme();
  const anchorRef = React.useRef(null);

  const [peekOpen, setPeekOpen] = React.useState(false);
  // Rotacion de icono
  const [icon, setIcon] = React.useState(false); 
  const [peek, setPeek] = React.useState({ prev: '', current: '', next: '' });
  const closeTimer = React.useRef(null);

  // Limpieza de timers
  React.useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Si el menú abre, cerramos peek y bloqueamos inputs (ya se respeta en handlers)
  React.useEffect(() => {
    if (isMenuOpen) {
      setPeekOpen(false);
      clearTimeout(closeTimer.current);
    }
  }, [isMenuOpen]);

  const showPeek = React.useCallback((prev, current, next) => {
    setPeek({ prev, current, next });
    setPeekOpen(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPeekOpen(false), peekDurationMs);
  }, [peekDurationMs]);

  const getIdx = React.useCallback(() => {
    if (!Array.isArray(items) || items.length === 0) return 0;
    if (Number.isInteger(valueIndex)) return mod(valueIndex, items.length);
    const idxRaw = items.findIndex(i => norm(i) === norm(value));
    return idxRaw === -1 ? 0 : idxRaw;
  }, [items, valueIndex, value]);

  const step = React.useCallback((dir) => {
    if (!Array.isArray(items) || items.length <= 1) return;
    //const idxRaw = items.findIndex(i => norm(i) === norm(value));
    //const idx = idxRaw === -1 ? 0 : idxRaw;
    const idx = getIdx();
    const nextIdx = mod(idx + dir, items.length);
    const prevIdx = mod(idx - 1, items.length);

    showPeek(items[prevIdx], items[idx], items[nextIdx]);
    onChange(items[nextIdx]);

    // Notifica por label y por índice (ambos, para transición suave)
    onChange?.(items[nextIdx]);
    onChangeIndex?.(nextIdx);

    
  }, [items, value, onChange, showPeek]);

  // Mostrar por índice si está disponible (evita des-sync con duplicados)
  const displayedText = Number.isInteger(valueIndex)
    ? (items?.[mod(valueIndex, items.length)] ?? value)
    : value;

  const onWheel = useWheelThrottled((e) => {
    if (isMenuOpen) return;           // bloqueado si el menú está abierto
    if (!Array.isArray(items) || items.length <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    const dir = e.deltaY > 0 ? +1 : -1;
    step(dir);
  }, 160);

  const onKeyDown = React.useCallback((e) => {
    if (isMenuOpen) return;
    if (!Array.isArray(items) || items.length <= 1) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); step(+1); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); step(-1); }
  }, [isMenuOpen, items, step]);

  // ⚠️ Ancla SIEMPRE al propio contenedor (no dependemos del event de React)
  const handleClick = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    onOpen?.({ currentTarget: el });
  }, [onOpen]);

  React.useEffect(() => {
    if (isMenuOpen) {
      setIcon(true); // Abierto → rotar hacia abajo
    } else {
      setIcon(false); // Cerrado → rotar hacia arriba
    }
  }, [isMenuOpen]);

  return (
    <Box
      ref={anchorRef}
      role="button"
      aria-haspopup="menu"
      tabIndex={0}
      onClick={handleClick}
      onWheel={onWheel}
      onKeyDown={onKeyDown}
      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative', outline: 'none' }}
    >
      <Typography
        component="h3"
        sx={{ color: 'rgba(255,255,255,0.47)', userSelect: 'none', ...textSx }}
      >
        {prefix}{displayedText} {/* {value} */}
      </Typography>
      
      <ArrowDropDownIcon className='icon' 
        sx={{
          transform: icon ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.3s ease',
          fontSize:'.85vw',
          color:"#e84e0e"
        }}
      />

      <Popper
        open={peekOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: (theme?.zIndex?.tooltip ?? 1500) + 1, pointerEvents: 'none' }}
        className='card-simple'
      >
        <Fade in={peekOpen} timeout={180}>
          <Box
            sx={{
              bgcolor: 'rgba(20,20,20,0.9)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px',
              px: '.5vw', py: '.5vh',
              backdropFilter: 'blur(2px)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
              minWidth: '12ch',
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.55, display: 'block', lineHeight: 1.4 }}>
              {peek.prev}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.45 }}>
              {peek.current}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.55, display: 'block', lineHeight: 1.4 }}>
              {peek.next}
            </Typography>
          </Box>
        </Fade>
      </Popper>
    </Box>
  );
};
